import { computed, onMounted, ref, unref, watch } from 'vue'
import { FilterMatchMode } from 'primevue/api'
import type {
  DataTableExpandedRows,
  DataTableFilterMeta,
} from 'primevue/datatable'
import { useConfirm } from 'primevue/useconfirm'
import dayjs from 'dayjs'
import useNotifications from '@/store/notification'
import useLoading from '@/store/load'
import { getApprovalFlowStepsByRequest } from '@/modules/approvers/services/ApprovalRequestService'
import { getFileUrlPresigned } from '@/shared/services/fileService'
import { formatDate } from '@/shared/utils/formatDate'
import type License from '../models/License'
import type Vacation from '../models/Vacation'
import type { WorkingDay } from '../models/WokingDay'
import type { Holiday } from '../models/Holiday'
import type GetLicenseTypeDto from '../services/interfaces/GetLicenseTypeDto'
import type LicenseForm from '../models/LicenseForm'
import {
  createLicenseRequest,
  deleteLicenseById,
  getDateEnd,
  getDaysByLicenseType,
  getLicenseTypes,
  getLicensesByEmployeeId,
} from '../services/LicenseService'
import { getVacationsByEmployeeId } from '../services/vacationService'
import type { ComputedRef, Ref } from 'vue'

interface UseLicensesOptions {
  employeeId?: Ref<string | undefined> | ComputedRef<string | undefined>
  autoLoad?: boolean
  fromRRHH?: boolean
}

const defaultFilters = (): DataTableFilterMeta => ({
  globalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
})

const buildLicenseForm = (
  employeeId?: string,
  fromRRHH?: boolean
): LicenseForm => ({
  dateInit: undefined,
  dateEnd: undefined,
  quantityDays: 0,
  origin: fromRRHH ? 'RRHH' : 'NATIVE',
  observation: '',
  employeeId: employeeId || '',
  licenseType: null,
  files: [],
  licenseNature: null,
  daysDetail: [],
})

export function useLicenses(options: UseLicensesOptions = {}) {
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const loadingStore = useLoading()

  const licenses = ref<License[]>([])
  const vacations = ref<Vacation[]>([])
  const statuses = ref(['PENDING', 'APPROVED', 'REJECTED'])
  const licenseDialog = ref(false)
  const submitted = ref(false)
  const workinDays = ref<WorkingDay[]>([])
  const holidays = ref<Holiday[]>([])
  const vacationsPending = ref<Date[]>([])
  const vacationsApproved = ref<Date[]>([])
  const licensesApproved = ref<Date[]>([])
  const licensesPending = ref<Date[]>([])
  const licenseDefinitions = ref<GetLicenseTypeDto>()
  const filters = ref<DataTableFilterMeta>(defaultFilters())
  const expandedRows = ref<DataTableExpandedRows | any[]>([] as any[])
  const license = ref<LicenseForm>(buildLicenseForm(unref(options.employeeId)))

  const resolveEmployeeId = (override?: string) =>
    override ?? unref(options.employeeId)

  const clearDialog = () => {
    license.value = {
      dateInit: undefined,
      dateEnd: undefined,
      quantityDays: 0,
      origin: options.fromRRHH ? 'RRHH' : 'NATIVE',
      observation: '',
      employeeId: resolveEmployeeId() || '',
      licenseType: license.value.licenseType,
      files: [],
      licenseNature: null,
      daysDetail: [],
    }
  }

  const refreshData = async (employeeIdOverride?: string) => {
    const employeeId = resolveEmployeeId(employeeIdOverride)
    if (!employeeId) {
      licenses.value = []
      vacations.value = []
      expandedRows.value = [] as any[]
      return
    }

    loadingStore.setLoadingState()
    try {
      const [licensesResp, vacationResp] = await Promise.all([
        getLicensesByEmployeeId(employeeId),
        getVacationsByEmployeeId(employeeId),
      ])

      licenses.value = licensesResp.data.map((l) => ({
        ...l,
        dateInit: formatDate(l.dateInit),
        dateEnd: formatDate(l.dateEnd),
        createdAt: dayjs(l.createdAt).format('DD/MM/YYYY HH:mm'),
      }))

      console.log('Licenses loaded:', licenses.value)

      vacations.value = vacationResp.data
      filters.value = defaultFilters()
      expandedRows.value = [] as any[]
      submitted.value = false
      licenseDialog.value = false
      license.value = buildLicenseForm(employeeId, options.fromRRHH)
    } finally {
      loadingStore.dismissLoadingState()
    }
  }

  const saveFile = (event: any) => {
    const files = event?.files
    if (!files) return
    license.value.files = files
  }

  const removeFile = () => {
    license.value.files = []
  }

  const onRowExpand = async (event: any) => {
    if (event?.data?.origin !== 'NATIVE') {
      return
    }

    const employeeId = resolveEmployeeId()
    if (!employeeId) return

    const requests = await getApprovalFlowStepsByRequest({
      approvalFlowId: event.data.approvalFlowId,
      approvalFlowRequestId: event.data.approvalFlowRequestId,
      employeeId,
    })

    licenses.value = licenses.value?.map((licenseItem: any) =>
      licenseItem.id === event.data.id
        ? {
            ...licenseItem,
            steps: requests,
          }
        : licenseItem
    )
  }

  const downloadAttachment = async (fileKey: string) => {
    if (!fileKey) return

    if (fileKey.includes('http')) {
      window.open(fileKey, '_blank')
      return
    }

    const fileUrl = await getFileUrlPresigned(fileKey)
    window.open(fileUrl, '_blank')
  }

  const onLicenseTypeChange = async () => {
    loadingStore.setLoadingState()
    try {
      const response = await getDaysByLicenseType({
        employeeId: resolveEmployeeId() || '',
        licenseTypeId: license.value.licenseType?.id || '',
      })
      holidays.value = response.holidays
      workinDays.value = response.workingDays
      clearDialog()
    } finally {
      loadingStore.dismissLoadingState()
    }
  }

  const deleteConfirm = (event: any, id: string) => {
    confirm.require({
      target: event?.currentTarget as HTMLElement,
      message: '¿Estás  seguro de eliminar esta solicitud?',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger p-button-sm',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: async () => {
        const response = await deleteLicenseById(id)
        if (response === 'OK') {
          await refreshData()
          notificationStore.addSuccesNotification({
            detail: '200',
            severity: 'success',
            summary: 'Solicitud de licencia eliminada con exito',
          })
        }
      },
      reject: () => {},
    })
  }

  const formatRequestState = (state: string) => {
    switch (state) {
      case 'PENDING':
        return 'Pendiente'
      case 'REJECTED':
        return 'Rechazado'
      case 'APPROVED':
        return 'Aprobado'
      default:
        return 'Pendiente'
    }
  }

  const openNewRequestDialog = async () => {
    licenseDialog.value = true
    if (workinDays.value?.length === 0 && holidays.value?.length === 0) {
      loadingStore.setLoadingState()
      try {
        const response = await getLicenseTypes(resolveEmployeeId() || '')
        licenseDefinitions.value = response
      } finally {
        loadingStore.dismissLoadingState()
      }
    }
  }

  const getBadge = (dateRaw: any) => {
    const date = new Date(dateRaw.year, dateRaw.month, dateRaw.day)

    if (
      vacationsPending.value.some(
        (pendingDate) => pendingDate.getTime() === date.getTime()
      )
    ) {
      return 'vacation-pending'
    }

    if (
      vacationsApproved.value.some(
        (approvedDate) => approvedDate.getTime() === date.getTime()
      )
    ) {
      return 'vacation-approved'
    }

    if (
      licensesPending.value.some(
        (pendingDate) => pendingDate.getTime() === date.getTime()
      )
    ) {
      return 'license-pending'
    }

    if (
      licensesApproved.value.some(
        (approvedDate) => approvedDate.getTime() === date.getTime()
      )
    ) {
      return 'license-approved'
    }

    const dateString = date.toISOString().split('T')[0]

    if (holidays.value?.find((h) => h.date.split('T')[0] === dateString)) {
      return 'holiday'
    }

    return ''
  }

  const saveRequest = async () => {
    loadingStore.setLoadingState()
    try {
      const response = await createLicenseRequest({
        dateInit: license.value.dateInit || new Date(),
        dateEnd: license.value.dateEnd || new Date(),
        daysDetail: license.value.daysDetail,
        quantityDays: license.value.quantityDays,
        origin: options.fromRRHH ? 'RRHH' : 'NATIVE',
        observation: license.value.observation,
        employeeId: resolveEmployeeId() || '',
        licenseTypeId: license.value.licenseType?.id || '',
        licenseType: license.value.licenseType?.name || '',
        licenseNatureId: license.value.licenseNature?.id || '',
        licenseNature: license.value.licenseNature?.name || '',
        files: license.value.files,
      })

      if (response === 'OK') {
        notificationStore.addSuccesNotification({
          detail: '200',
          severity: 'success',
          summary: `Solicitud de ${license.value.licenseType?.name} creada con exito`,
        })

        await refreshData()
      }
    } finally {
      loadingStore.dismissLoadingState()
      hideDialog()
    }
  }

  const hideDialog = () => {
    clearDialog()
    licenseDialog.value = false
  }

  const calculateDateEnd = async () => {
    setTimeout(async () => {
      if (
        license.value.quantityDays > 0 &&
        license.value.dateInit &&
        license.value.dateInit.getTime() > 0
      ) {
        loadingStore.setLoadingState()
        try {
          const response = await getDateEnd({
            employeeId: resolveEmployeeId() || '',
            licenseTypeId: license.value.licenseType?.id || '',
            days: String(license.value.quantityDays),
            startDate: license.value.dateInit.toISOString().split('T')[0],
          })

          license.value.dateEnd = new Date(response.endDate + 'T00:00:00')

          const datesNoAvailables = [
            ...vacationsPending.value,
            ...vacationsApproved.value,
            ...licensesPending.value,
            ...licensesApproved.value,
          ].map((date) => date.toISOString().split('T')[0])

          if (
            response.dayToDay.some((date) => datesNoAvailables.includes(date))
          ) {
            notificationStore.addSuccesNotification({
              detail: '400',
              severity: 'warn',
              summary:
                'Hay fechas no disponibles en el rango de fechas seleccionado',
            })

            license.value.dateEnd = undefined
            license.value.quantityDays = 0
          }
          license.value.daysDetail = response.dayToDay
        } finally {
          loadingStore.dismissLoadingState()
        }
      }
    }, 300)
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'

      case 'PENDING':
        return 'warning'

      case 'REJECTED':
        return 'danger'

      default:
        return 'warning'
    }
  }

  const getDisabledDays = computed(() => {
    const allDays = [1, 2, 3, 4, 5, 6, 0]

    workinDays.value?.forEach((day) => {
      if (day.isoWeekDay === 7) {
        day.isoWeekDay = 0
      }
      if (
        typeof day.isoWeekDay === 'number' &&
        allDays.includes(day.isoWeekDay)
      ) {
        const index = allDays.indexOf(day.isoWeekDay)
        if (index !== -1) {
          allDays.splice(index, 1)
        }
      }
    })

    return allDays
  })

  const getDisabledDates = computed(() => {
    const disabledDates: Date[] = []

    holidays.value?.forEach((holiday) => {
      disabledDates.push(new Date(holiday.date.split('T')[0] + 'T00:00:00'))
    })

    vacationsPending.value = []
    vacationsApproved.value = []
    licensesPending.value = []
    licensesApproved.value = []

    vacations.value
      ?.filter((v) => v.globalStatus === 'PENDING')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          const date = new Date(d + 'T00:00:00')
          disabledDates.push(date)
          vacationsPending.value.push(date)
        })
      })

    vacations.value
      ?.filter((v) => v.globalStatus === 'APPROVED')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          const date = new Date(d + 'T00:00:00')
          disabledDates.push(date)
          vacationsApproved.value.push(date)
        })
      })

    licenses.value
      ?.filter((v) => v.globalStatus === 'PENDING')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          const date = new Date(d + 'T00:00:00')
          disabledDates.push(date)
          licensesPending.value.push(date)
        })
      })

    licenses.value
      ?.filter((v) => v.globalStatus === 'APPROVED')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          const date = new Date(d + 'T00:00:00')
          disabledDates.push(date)
          licensesApproved.value.push(date)
        })
      })

    return disabledDates
  })

  const isValidToSave = computed(() => {
    const licenseType = license.value.licenseType
    const requiresAttachment =
      licenseType?.allow_primary_attachment &&
      licenseType?.obligatory_primary_attachment

    return (
      !!license.value.dateInit &&
      !!license.value.dateEnd &&
      license.value.quantityDays > 0 &&
      !!license.value.observation &&
      !!licenseType &&
      (requiresAttachment ? license.value.files.length > 0 : true)
    )
  })

  onMounted(() => {
    if (options.autoLoad ?? true) {
      refreshData()
    }
  })

  watch(
    () => unref(options.employeeId),
    (newId, oldId) => {
      if ((options.autoLoad ?? true) && newId !== oldId) {
        refreshData(newId)
      }
    }
  )

  return {
    // state
    license,
    licenses,
    vacations,
    statuses,
    licenseDialog,
    submitted,
    licenseDefinitions,
    filters,
    expandedRows,

    // computed helpers
    getDisabledDays,
    getDisabledDates,
    getBadge,
    isValidToSave,

    // actions
    refreshData,
    saveFile,
    removeFile,
    openNewRequestDialog,
    onRowExpand,
    downloadAttachment,
    onLicenseTypeChange,
    deleteConfirm,
    formatRequestState,
    saveRequest,
    hideDialog,
    clearDialog,
    calculateDateEnd,
    getStatusLabel,
  }
}
